// client/src/components/timesheet/TimesheetForm.jsx (updated with table and action buttons)
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Tabs,
  Tab,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Save as SaveIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Work as WorkIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Pending as PendingIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

dayjs.extend(utc);
dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const TimesheetForm = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0); // 0: List View, 1: Manual Entry, 2: Excel Upload
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [employeeId, setEmployeeId] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Location state
  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
    address: '',
    loading: false,
    error: null,
    permissionDenied: false
  });

  // Manual form state
  const [formData, setFormData] = useState({
    date: dayjs(),
    task_name: '',
    hours_worked: '',
    description: '',
    location_address: ''
  });

  // Excel upload state
  const [excelFile, setExcelFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Timesheet list state
  const [timesheets, setTimesheets] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [summary, setSummary] = useState({
    total_hours: 0,
    pending_count: 0,
    approved_count: 0,
    rejected_count: 0
  });

  // View/Edit Dialog states
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editFormData, setEditFormData] = useState({
    task_name: '',
    hours_worked: '',
    description: '',
    location_address: ''
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [companyData, setCompanyData] = useState({ dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Kolkata' });

  // Get current user on component mount
  useEffect(() => {
    fetchCurrentUser();
  }, []);

  // Get location when form loads
  useEffect(() => {
    if (activeTab === 1) {
      getCurrentLocation();
    }
  }, [activeTab]);

  // Fetch timesheets when on list tab
  useEffect(() => {
    if (activeTab === 0 && employeeId && companyId) {
      fetchTimesheets();
    }
  }, [activeTab, employeeId, companyId, pagination.page, pagination.limit]);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      setCompanyId(company_id);
  
      try {
        const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
          withCredentials: true
        });
  
        if (response.data && response.data.employee_id) {
          setEmployeeId(response.data.employee_id);
        }

        // Fetch company specific settings
        try {
          const cmpRes = await axios.get(`${API_URL}/api/cmp/${company_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (cmpRes.data && cmpRes.data.data) {
            let df = cmpRes.data.data.dateFormat || 'DD/MM/YYYY';
            let tz = cmpRes.data.data.timezone || 'Asia/Kolkata';
            setCompanyData({ dateFormat: df, timezone: tz });
          }
        } catch(e) {
          // console.error("Could not fetch company config", e);
        }
      } catch (apiErr) {
        const employee_id = payload.employee_id || payload.id || payload.userId;
        if (employee_id) {
          setEmployeeId(employee_id);
        }
      }
    } catch (err) {
      //console.error("Error fetching current user:", err);
      showSnackbar('Failed to fetch user information', 'error');
    }
  };

  const fetchTimesheets = async () => {
    setLoading(true);
    try {
      const params = {
        company_id: companyId,
        employee_id: employeeId,
        page: pagination.page + 1,
        limit: pagination.limit
      };

      const response = await axios.get(`${API_URL}/api/timesheets/employee`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setTimesheets(response.data.data.timesheets || []);
        setSummary(response.data.data.summary || {});
        setPagination({
          page: pagination.page,
          limit: pagination.limit,
          total: response.data.data.pagination?.total || 0,
          pages: response.data.data.pagination?.pages || 0
        });
      }
    } catch (error) {
      //console.error('Error fetching timesheets:', error);
      showSnackbar('Failed to fetch timesheets', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocation(prev => ({
        ...prev,
        error: 'Geolocation is not supported by your browser',
        permissionDenied: true
      }));
      return;
    }

    setLocation(prev => ({ ...prev, loading: true, error: null }));

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        setLocation(prev => ({
          ...prev,
          latitude,
          longitude,
          loading: false,
          error: null,
          permissionDenied: false
        }));

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1&accept-language=en`       
          );
          const data = await response.json();
          
          if (data.display_name) {
            const address = data.display_name;
            setLocation(prev => ({ ...prev, address }));
            setFormData(prev => ({ ...prev, location_address: address }));
          }
        } catch (err) {
          //console.error("Error getting address:", err);
          setLocation(prev => ({ 
            ...prev, 
            address: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
          }));
        }
      },
      (error) => {
        let errorMessage = '';
        let permissionDenied = false;
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access.';
            permissionDenied = true;
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
          default:
            errorMessage = 'An unknown error occurred while getting location.';
        }
        
        setLocation(prev => ({
          ...prev,
          latitude: null,
          longitude: null,
          address: '',
          loading: false,
          error: errorMessage,
          permissionDenied
        }));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Manual form handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleDateChange = (newDate) => {
    setFormData({
      ...formData,
      date: newDate
    });
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.hours_worked || formData.hours_worked < 0.25 || formData.hours_worked > 24) {
      showSnackbar('Hours must be between 0.25 and 24', 'error');
      return;
    }
  
    if (!formData.description || formData.description.trim().length < 10) {
      showSnackbar('Description must be at least 10 characters', 'error');
      return;
    }
  
    if (!formData.task_name) {
      showSnackbar('Please enter a task name', 'error');
      return;
    }
  
    try {
      setSubmitting(true);
  
      const submitData = {
        employee_id: employeeId,
        date: formData.date.toISOString(),
        task_name: formData.task_name,
        hours_worked: parseFloat(formData.hours_worked),
        description: formData.description.trim(),
        latitude: location.latitude,
        longitude: location.longitude,
        location_address: formData.location_address || location.address || '',
        user_agent: navigator.userAgent
      };
  
      const response = await axios.post(
        `${API_URL}/api/timesheets`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,"content-type": "application/json"
          }
        }
      );
  
      if (response.data.success) {
        showSnackbar('Timesheet submitted successfully!', 'success');
        setFormData({
          date: dayjs(),
          task_name: '',
          hours_worked: '',
          description: '',
          location_address: ''
        });
        
        // Switch to list tab
        setActiveTab(0);
        
        // Refresh the list immediately
        await fetchTimesheets();
      }
    } catch (err) {
      //console.error("Error submitting timesheet:", err);
      showSnackbar(err.response?.data?.message || 'Failed to submit timesheet', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Excel upload handlers
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/timesheets/excel/template`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'timesheet_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();

      showSnackbar('Template downloaded successfully', 'success');
    } catch (error) {
      //console.error('Error downloading template:', error);
      showSnackbar('Failed to download template', 'error');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.oasis.opendocument.spreadsheet',
        'text/csv'
      ];
      const fileExt = file.name.split('.').pop().toLowerCase();
      const validExt = ['xlsx', 'xls', 'csv'];

      if (!validTypes.includes(file.type) && !validExt.includes(fileExt)) {
        showSnackbar('Please upload a valid Excel file (.xlsx, .xls, or .csv)', 'error');
        e.target.value = '';
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        showSnackbar('File size should be less than 5MB', 'error');
        e.target.value = '';
        return;
      }

      setExcelFile(file);
      setUploadResults(null);
    }
  };

  const handleUploadSubmit = async () => {
    if (!excelFile) {
      showSnackbar('Please select a file to upload', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('file', excelFile);
    formData.append('employee_id', employeeId);

    try {
      setUploading(true);
      setUploadProgress(0);

      const response = await axios.post(
        `${API_URL}/api/timesheets/excel/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );

      if (response.data.success) {
        setUploadResults(response.data.data);
        showSnackbar(response.data.message, response.data.data.error_count > 0 ? 'warning' : 'success');
      }
    } catch (err) {
      //console.error('Error uploading file:', err);
      showSnackbar(err.response?.data?.message || 'Failed to upload file', 'error');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClearFile = () => {
    setExcelFile(null);
    setUploadResults(null);
    document.getElementById('excel-file-input').value = '';
  };

  // View/Edit/Delete handlers
  const handleViewTimesheet = (timesheet) => {
    setSelectedTimesheet(timesheet);
    setOpenViewDialog(true);
  };

  const handleEditTimesheet = (timesheet) => {
    setSelectedTimesheet(timesheet);
    setEditFormData({
      task_name: timesheet.task_name || '',
      hours_worked: timesheet.hours_worked || '',
      description: timesheet.description || '',
      location_address: timesheet.location_address || ''
    });
    setOpenEditDialog(true);
  };

  const handleUpdateTimesheet = async () => {
    try {
      const response = await axios.put(
        `${API_URL}/api/timesheets/${selectedTimesheet.timesheet_id}`,
        editFormData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,"content-type": "application/json"
          }
        }
      );

      if (response.data.success) {
        showSnackbar('Timesheet updated successfully', 'success');
        setOpenEditDialog(false);
        fetchTimesheets();
      }
    } catch (error) {
      //console.error('Error updating timesheet:', error);
      showSnackbar('Failed to update timesheet', 'error');
    }
  };

  const handleDeleteTimesheet = async () => {
    setDeleteLoading(true);
    try {
      const response = await axios.delete(
        `${API_URL}/api/timesheets/${selectedTimesheet.timesheet_id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,"content-type": "application/json"
          }
        }
      );

      if (response.data.success) {
        showSnackbar('Timesheet deleted successfully', 'success');
        setOpenDeleteDialog(false);
        fetchTimesheets();
      }
    } catch (error) {
      //console.error('Error deleting timesheet:', error);
      showSnackbar('Failed to delete timesheet', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event) => {
    setPagination(prev => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      page: 0
    }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      'Pending': { icon: <PendingIcon />, color: 'warning', label: 'Pending' },
      'Approved': { icon: <ApprovedIcon />, color: 'success', label: 'Approved' },
      'Rejected': { icon: <RejectedIcon />, color: 'error', label: 'Rejected' },
      'Resubmitted': { icon: <RefreshIcon />, color: 'info', label: 'Resubmitted' }
    };

    const config = statusConfig[status] || statusConfig['Pending'];
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        size="small"
        color={config.color}
        variant="outlined"
      />
    );
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header with Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight="bold">
            Timesheet 
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setActiveTab(1)}
              sx={{ bgcolor: 'primary.main' }}
            >
              Add Timesheet
            </Button>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => setActiveTab(2)}
              sx={{ bgcolor: 'success.main' }}
            >
              Excel Upload
            </Button>
          </Box>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#e3f2fd' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Hours
                </Typography>
                <Typography variant="h4">{summary.total_hours?.toFixed(1)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#fff3e0' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Pending
                </Typography>
                <Typography variant="h4">{summary.pending_count}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#e8f5e8' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Approved
                </Typography>
                <Typography variant="h4">{summary.approved_count}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#ffebee' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Rejected
                </Typography>
                <Typography variant="h4">{summary.rejected_count}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
              <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Timesheet List" icon={<WorkIcon />} iconPosition="start" />
          <Tab label="Manual Entry" icon={<AddIcon />} iconPosition="start" />
          <Tab label="Excel Upload" icon={<UploadIcon />} iconPosition="start" />
        </Tabs>


        <Divider sx={{ mb: 4 }} />

        {/* Timesheet List Tab */}
        {activeTab === 0 && (
          <>
            {/* Refresh Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchTimesheets}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>

            {/* Timesheet Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell>Date</TableCell>
                    <TableCell>Task Name</TableCell>
                    <TableCell>Hours</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : timesheets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Typography color="textSecondary">
                          No timesheets found. Click "Add Timesheet" to create one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    timesheets.map((timesheet) => (
                      <TableRow key={timesheet.timesheet_id} hover>
                        <TableCell>
                          {timesheet.date ? format(new Date(timesheet.date), companyData.dateFormat) : '-'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {timesheet.task_name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${timesheet.hours_worked} hrs`}
                            size="small"
                            color={timesheet.is_overtime ? 'warning' : 'default'}
                            icon={<TimeIcon />}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={timesheet.description || ''}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                maxWidth: 200, 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {timesheet.description || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {timesheet.location_address ? (
                            <Tooltip title={timesheet.location_address}>
                              <LocationIcon color="action" fontSize="small" />
                            </Tooltip>
                          ) : timesheet.latitude && timesheet.longitude ? (
                            <Tooltip title={`${timesheet.latitude}, ${timesheet.longitude}`}>
                              <LocationIcon color="action" fontSize="small" />
                            </Tooltip>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {getStatusChip(timesheet.status)}
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleViewTimesheet(timesheet)}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {timesheet.status === 'Pending' && (
                              <>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleEditTimesheet(timesheet)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      setSelectedTimesheet(timesheet);
                                      setOpenDeleteDialog(true);
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={pagination.total}
              page={pagination.page}
              onPageChange={handlePageChange}
              rowsPerPage={pagination.limit}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}

        {/* Manual Entry Tab */}
        {activeTab === 1 && (
          <>
            {location.loading && (
              <Alert icon={<CircularProgress size={20} />} severity="info" sx={{ mb: 3 }}>
                Getting your location...
              </Alert>
            )}

            {location.error && !location.loading && (
              <Alert 
                severity="warning" 
                sx={{ mb: 3 }}
                action={
                  <Button color="inherit" size="small" onClick={getCurrentLocation}>
                    Try Again
                  </Button>
                }
              >
                {location.error}
                {location.permissionDenied && (
                  <Typography variant="caption" display="block">
                    You can manually enter your location below.
                  </Typography>
                )}
              </Alert>
            )}

            <form onSubmit={handleManualSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      label="Date & Time"
                      value={formData.date}
                      onChange={handleDateChange}
                      timezone={companyData.timezone}
                      format={`${companyData.dateFormat} hh:mm A`}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Task Name"
                    name="task_name"
                    value={formData.task_name}
                    onChange={handleChange}
                    placeholder="e.g., Development work, Client meeting"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label="Hours Worked"
                    name="hours_worked"
                    value={formData.hours_worked}
                    onChange={handleChange}
                    inputProps={{ step: 0.25, min: 0.25, max: 24 }}
                    InputProps={{
                      startAdornment: <TimeIcon sx={{ mr: 1, color: 'action.active' }} />
                    }}
                    helperText="Enter in decimal (e.g., 4.5 for 4h 30m)"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={4}
                    label="Work Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Describe what you worked on in detail..."
                    helperText="Minimum 10 characters"
                    InputProps={{
                      startAdornment: <WorkIcon sx={{ mr: 1, color: 'action.active', alignSelf: 'flex-start', mt: 1 }} />
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Location Address (Optional)"
                    name="location_address"
                    value={formData.location_address}
                    onChange={handleChange}
                    placeholder="Enter your work location or address"
                    InputProps={{
                      startAdornment: <LocationIcon sx={{ mr: 1, color: 'action.active' }} />
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => setActiveTab(0)}
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
                      disabled={submitting || !employeeId}
                    >
                      {submitting ? 'Submitting...' : 'Submit Timesheet'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </>
        )}

        {/* Excel Upload Tab */}
        {activeTab === 2 && (
          <>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1">
                Download the template, fill in your timesheet data, and upload the file.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
              >
                Download Template
              </Button>
            </Box>

            <Paper variant="outlined" sx={{ p: 3, bgcolor: '#f9f9f9', mb: 4 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Upload Excel File
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="contained"
                      component="label"
                      startIcon={<UploadIcon />}
                      disabled={uploading}
                    >
                      Choose File
                      <input
                        id="excel-file-input"
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        hidden
                        onChange={handleFileChange}
                      />
                    </Button>
                    {excelFile && (
                      <>
                        <Chip
                          label={excelFile.name}
                          onDelete={handleClearFile}
                          deleteIcon={<DeleteIcon />}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {(excelFile.size / 1024).toFixed(2)} KB
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>

                {excelFile && !uploadResults && (
                  <Grid item xs={12}>
                    {uploading ? (
                      <Box sx={{ width: '100%' }}>
                        <LinearProgress variant="determinate" value={uploadProgress} />
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                          Uploading... {uploadProgress}%
                        </Typography>
                      </Box>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUploadSubmit}
                        startIcon={<UploadIcon />}
                      >
                        Upload and Process
                      </Button>
                    )}
                  </Grid>
                )}

                {uploadResults && (
                  <Grid item xs={12}>
                    <Alert 
                      severity={uploadResults.error_count > 0 ? 'warning' : 'success'}
                      sx={{ mb: 2 }}
                    >
                      <Typography variant="subtitle2">
                        Processed {uploadResults.success_count} of {uploadResults.total_rows} entries successfully
                      </Typography>
                      {uploadResults.error_count > 0 && (
                        <Typography variant="body2">
                          {uploadResults.error_count} entries failed. See details below.
                        </Typography>
                      )}
                    </Alert>

                    {uploadResults.successful_entries?.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          <SuccessIcon color="success" sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                          Successful Entries ({uploadResults.successful_entries.length})
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Row</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Task</TableCell>
                                <TableCell>Hours</TableCell>
                                <TableCell align="right">Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {uploadResults.successful_entries.map((entry, index) => (
                                <TableRow key={index}>
                                  <TableCell>{entry.row}</TableCell>
                                  <TableCell>{entry.date}</TableCell>
                                  <TableCell>{entry.task_name}</TableCell>
                                  <TableCell>{entry.hours}</TableCell>
                                  <TableCell align="right">
                                    <Chip 
                                      label="Created" 
                                      size="small" 
                                      color="success"
                                      icon={<SuccessIcon />}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {uploadResults.errors?.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom color="error">
                          <ErrorIcon color="error" sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                          Errors ({uploadResults.errors.length})
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Row</TableCell>
                                <TableCell>Error</TableCell>
                                <TableCell>Data</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {uploadResults.errors.map((error, index) => (
                                <TableRow key={index}>
                                  <TableCell>{error.row}</TableCell>
                                  <TableCell>
                                    <Typography color="error">{error.message}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title="View Data">
                                      <IconButton size="small">
                                        <ViewIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                      <Button variant="outlined" onClick={handleClearFile}>
                        Upload Another File
                      </Button>
                      <Button variant="contained" onClick={() => setActiveTab(0)}>
                        View All Timesheets
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>

            <Card sx={{ bgcolor: 'background.default' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Excel File Format Instructions:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" component="div">
                      <strong>Required Columns:</strong>
                      <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                        <li>Date (YYYY-MM-DD)</li>
                        <li>Task Name</li>
                        <li>Hours Worked (0.25 - 24)</li>
                        <li>Description (min 10 characters)</li>
                      </ul>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" component="div">
                      <strong>Optional Columns:</strong>
                      <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                       
                        <li>Location Address</li>
                      </ul>
                    </Typography>
                  </Grid>
                </Grid>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    <strong>Note:</strong> Maximum 5MB file size. Supported formats: .xlsx, .xls, .csv
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          </>
        )}

        {/* Info Card - Common */}
        <Card sx={{ mt: 4, bgcolor: 'background.default' }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Tips for timesheet management:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Hours are rounded to nearest 15 minutes (0.25 hour increments)
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Maximum 24 hours per day
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Only pending timesheets can be edited or deleted
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Timesheets are sent to your reporting manager for approval
                </Typography>
              </li>
            </ul>
          </CardContent>
        </Card>
      </Paper>

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
        {selectedTimesheet && (
          <>
            <DialogTitle>
              Timesheet Details
              <Typography variant="subtitle2" color="textSecondary">
                ID: {selectedTimesheet.timesheet_id}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Date</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedTimesheet.date ? format(new Date(selectedTimesheet.date), companyData.dateFormat) : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Box sx={{ mt: 0.5 }}>{getStatusChip(selectedTimesheet.status)}</Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Task Name</Typography>
                  <Typography variant="body1" gutterBottom>{selectedTimesheet.task_name || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Hours Worked</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedTimesheet.hours_worked} hrs
                    {selectedTimesheet.is_overtime && (
                      <Chip label="Overtime" size="small" color="warning" sx={{ ml: 1 }} />
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                    <Typography variant="body2">{selectedTimesheet.description}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Location</Typography>
                  <Typography variant="body2">
                    {selectedTimesheet.location_address || 
                     (selectedTimesheet.latitude && selectedTimesheet.longitude 
                       ? `${selectedTimesheet.latitude}, ${selectedTimesheet.longitude}`
                       : 'Location not available')}
                  </Typography>
                </Grid>
                {selectedTimesheet.review_comments && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Review Comments</Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#ffebee' }}>
                      <Typography variant="body2">{selectedTimesheet.review_comments}</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Timesheet</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Task Name"
                value={editFormData.task_name}
                onChange={(e) => setEditFormData({ ...editFormData, task_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Hours Worked"
                value={editFormData.hours_worked}
                onChange={(e) => setEditFormData({ ...editFormData, hours_worked: e.target.value })}
                inputProps={{ step: 0.25, min: 0.25, max: 24 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location Address"
                value={editFormData.location_address}
                onChange={(e) => setEditFormData({ ...editFormData, location_address: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateTimesheet}>Update</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Timesheet</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this timesheet? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDeleteTimesheet}
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TimesheetForm;